import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from 'react';
import {
	Container,
	Stack,
	AppBar,
	Toolbar,
	Typography,
	Dialog,
	IconButton,
	TableContainer,
	Table,
	TableCell,
	TableRow,
	TableHead,
	TableBody,
	Divider
} from '@mui/material';
import { isEmpty } from 'lodash';

import { useParams } from 'react-router-dom';
import { DataContext } from '../../../context/DataContext';

import { getRol, getToken, getUser } from '../../../api/Account';
import ReactToPrint from 'react-to-print';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import NoData from '../../../components/NoData';
import { getCreditosSaneadosAsync, getSaldoCarteraAsync } from '../../../api/Report';
import { PrintReport } from '../../../components/PrintReport';
import moment from 'moment';
import {
	getGestoresSaneadosAsync,
	getGestoresSaneadosBySucursalAccessAsync,
	getSucursalByUserAsync
} from '../../../api/Users';
import { simpleMessage } from '../../../helpers/Helpers';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowDown } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx/xlsx.mjs';

const CreditosSaneados = () => {
	const [data, setData] = useState([]);

	const [sucursalName, setSucursalName] = useState('');
	const [sucursalList, setSucursalList] = useState([]);

	const [gestorName, setGestorName] = useState('');
	const [gestorList, setGestorList] = useState([]);

	const compRef = useRef();
	const { selectedSucursal, selectedGestor, moneda, tipoCredito } = useParams();

	const { setIsLoading, setIsUnautorized, title, slogan, icon } = useContext(DataContext);

	const token = getToken();
	const user = getUser();
	const rol = getRol();

	const formatDate = (d) => moment(d).format('L');

	const formatMoney = (v, isDolar) =>
		new Intl.NumberFormat(isDolar ? 'en-US' : 'es-NI', {
			style: 'currency',
			currency: isDolar ? 'USD' : 'NIO'
		}).format(v);

	const requestPayload = useMemo(
		() => ({
			moneda,
			gestorId: selectedGestor === 't' ? 0 : Number(selectedGestor),
			sucursalId: selectedSucursal === 't' ? 0 : Number(selectedSucursal),
			tipoCredito: tipoCredito === 't' ? 0 : Number(tipoCredito)
		}),
		[moneda, selectedGestor, selectedSucursal, tipoCredito]
	);

	const fetchData = useCallback(async () => {
		let sucursals = await getSucursals();

		if (selectedSucursal === 't') {
			setSucursalName('Todas');
		} else {
			const suc = sucursals.find((s) => s.id.toString() === selectedSucursal);
			setSucursalName(suc?.description ?? '');
		}

		setIsLoading(true);

		const result = await getCreditosSaneadosAsync(token, requestPayload);

		setIsLoading(false);

		if (!result.statusResponse) {
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}

		setData(result.data.result);
	}, [requestPayload, selectedSucursal, token, setIsLoading, setIsUnautorized]);

	const hasFetchedRef = useRef(false);

	useEffect(() => {
		if (hasFetchedRef.current) return;
		hasFetchedRef.current = true;

		fetchData();
	}, [fetchData]);

	const getSucursals = async () => {
		setIsLoading(true);
		const result = await getSucursalByUserAsync(token);
		if (!result.statusResponse) {
			setIsLoading(false);
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}
		setIsLoading(false);
		setSucursalList(
			selectedSucursal === 't'
				? result.data.result
				: result.data.result.filter((g) => g.id.toString() === selectedSucursal)
		);

		let gestores = await getGestores(selectedSucursal);

		if (selectedGestor === 't') {
			setGestorName('Todos');
		} else {
			let name = gestores.filter((item) => item.id.toString() === selectedGestor);

			setGestorName(name[0].fullName);
		}
		return result.data.result;
	};

	const getGestores = async (sucursal) => {
		setIsLoading(true);
		const result =
			sucursal === 't'
				? await getGestoresSaneadosBySucursalAccessAsync(token)
				: await getGestoresSaneadosAsync(token, sucursal);
		if (!result.statusResponse) {
			setIsLoading(false);
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}

		setIsLoading(false);
		setGestorList(result.data.result);
		return result.data.result;
	};

	const pageStyle = `
    @media print {
      html, body {
        height: initial !important;
        overflow: initial !important;
        -webkit-print-color-adjust: exact;
      }
    }
    @media all {
      .pagebreak {
        display: none;
      }
    }
    @media print {
      .pagebreak {
        page-break-before: always;
      }
    }  
    @page { size: landscape;  margin: 2mm; }
  `;

	const exportExcel = () => {
		const exp = data.map((item) => {
			const {
				id,
				fullName,
				fechaEntrega,
				fechaVencimiento,
				cuotasPagadas,
				cuotasPendiente,
				montoEntregado,
				montoPagar,
				montoAbonado,
				montoPendiente,
				saldoCapital,
				saldoIC,
				saldoTotal,
				gestor,
				isDolar,
				ic
			} = item;

			const gestorData = gestorList.find((g) => g.id === gestor);

			return {
				'ID Crédito': id,
				Cliente: fullName,
				'Fecha Entrega': moment(fechaEntrega).format('L'),
				'Fecha Vencimiento': moment(fechaVencimiento).format('L'),
				'Cuotas Pagadas': cuotasPagadas,
				'Cuotas Pendientes': cuotasPendiente,
				'Monto Entregado': montoEntregado,
				'Interes C.': ic + '%',
				'Monto a Pagar': montoPagar,
				'Monto Abonado': montoAbonado,
				'Monto Pendiente': montoPendiente,
				'Saldo Capital': saldoCapital,
				'Saldo I.C.': saldoIC,
				'Saldo Total': saldoTotal,
				Moneda: isDolar ? 'USD' : 'NIO',
				Gestor: gestorData?.fullName ?? 'SIN GESTOR'
			};
		});

		const workbook = XLSX.utils.book_new();
		const worksheet = XLSX.utils.json_to_sheet(exp);

		XLSX.utils.book_append_sheet(workbook, worksheet, 'CreditosSaneados');
		XLSX.writeFile(workbook, 'CreditosSaneados.xlsx');
	};

	const paramText = (
		<Container maxWidth={false} sx={{ textAlign: 'center' }}>
			<Stack direction={'row'} justifyContent="space-between">
				<Stack>
					<Typography
						sx={{
							color: '#2196f3',
							textAlign: 'left'
						}}
						fontSize={11}
					>
						Parametros:
					</Typography>
					<Typography fontSize={11} textAlign="left">
						{`Sucursal: ${selectedSucursal === 't' ? 'Todas' : sucursalName}`}
					</Typography>
					<Typography fontSize={11} textAlign="left">
						{`Gestor Seleccionado: ${selectedGestor === 't' ? 'Todos' : gestorName}`}
					</Typography>

					<Typography fontSize={11} textAlign="left">
						{`Moneda: ${
							parseInt(moneda) === 1 ? 'Cordoba' : 'Dolar'
						} - Tipo de Crédito: ${
							tipoCredito === 't'
								? 'Todos'
								: tipoCredito === '1'
									? 'EMPEÑO'
									: 'CREDITO'
						}`}
					</Typography>
				</Stack>

				<Stack>
					<Typography
						sx={{
							color: '#2196f3',
							textAlign: 'left'
						}}
						fontSize={11}
					>
						Usuario:
					</Typography>
					<Typography fontSize={11} textAlign="left">
						{user}
					</Typography>
					<Typography fontSize={11} textAlign="left">
						{rol}
					</Typography>
					<Typography fontSize={11} textAlign="left">
						{`Fecha y Hora: ${moment().format('DD/MM/YYYY hh:mm A')}`}
					</Typography>
				</Stack>
			</Stack>
		</Container>
	);

	const DataTable = ({ dataGestor = [], headerFontSize, dataFontSize, isForPrint = false }) => {
		const columns = [
			{ key: 'id', label: '#', align: 'center', getValue: (r) => r.id },
			{
				key: 'fechaEntrega',
				label: 'F.Entrega',
				align: 'center',
				getValue: (r) => formatDate(r.fechaEntrega)
			},
			{
				key: 'fechaVencimiento',
				label: 'F.Vencimiento',
				align: 'center',
				getValue: (r) => formatDate(r.fechaVencimiento)
			},
			{
				key: 'fullName',
				label: 'Cliente',
				align: 'left',
				getValue: (r) => r.fullName
			},
			{
				key: 'estadoCuotas',
				label: 'Estado Cuotas',
				align: 'left',
				getValue: (r) => ({
					cuotasPagadas: r.cuotasPagadas,
					cuotasPendiente: r.cuotasPendiente
				})
			},
			{
				key: 'montoEntregado',
				label: 'M.Entregado',
				align: 'center',
				getValue: (r) => formatMoney(r.montoEntregado, r.isDolar)
			},
			{
				key: 'interesC.',
				label: 'Interes C.',
				align: 'center',
				getValue: (r) => r.ic + '%'
			},
			{
				key: 'montoPagar',
				label: 'M.Pagar',
				align: 'center',
				getValue: (r) => formatMoney(r.montoPagar, r.isDolar)
			},
			{
				key: 'montoAbonado',
				label: 'M.Abonado',
				align: 'center',
				getValue: (r) => formatMoney(r.montoAbonado, r.isDolar)
			},
			{
				key: 'montoPendiente',
				label: 'M.Pendiente',
				align: 'center',
				getValue: (r) => formatMoney(r.montoPendiente, r.isDolar)
			},
			{
				key: 'saldoCapital',
				label: 'Saldo C.',
				align: 'center',
				getValue: (r) => formatMoney(r.saldoCapital, r.isDolar)
			},
			{
				key: 'saldoIC',
				label: 'Saldo IC.',
				align: 'center',
				getValue: (r) => formatMoney(r.saldoIC, r.isDolar)
			},
			{
				key: 'saldoTotal',
				label: 'Saldo T.',
				align: 'center',
				getValue: (r) => formatMoney(r.saldoTotal, r.isDolar)
			}
		];

		const totals = useMemo(() => {
			if (!dataGestor.length) {
				return {
					totalCapital: 0,
					totalSaldoCapital: 0,
					totalSaldoIC: 0,
					totalSaldo: 0
				};
			}
			return dataGestor.reduce(
				(acc, c) => {
					acc.totalCapital += c.montoEntregado;
					acc.totalSaldoCapital += c.saldoCapital;
					acc.totalSaldoIC += c.saldoIC;
					acc.totalSaldo += c.saldoTotal;

					return acc;
				},
				{
					totalCapital: 0,
					totalSaldoCapital: 0,
					totalSaldoIC: 0,
					totalSaldo: 0
				}
			);
		}, [dataGestor]);
		const { totalCapital, totalSaldoCapital, totalSaldoIC, totalSaldo } = totals;

		return dataGestor.length === 0 ? (
			<>
				<Typography
					sx={{
						color: '#2196f3',
						textAlign: 'center',
						fontWeight: 'bold'
					}}
					fontSize={15}
				>
					Gestor no tiene créditos asignados!
				</Typography>
				<hr />
			</>
		) : (
			<>
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								{columns.map((col) => (
									<TableCell
										key={col.key}
										align={col.align}
										style={{ fontSize: headerFontSize }}
										className="text-primary fw-bold"
									>
										{col.label}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{dataGestor.map((r) => {
								return (
									<TableRow key={r.id}>
										{columns.map((col) => {
											if (col.key === 'estadoCuotas') {
												return (
													<TableCell
														key={col.key}
														align={col.align}
														style={{ fontSize: dataFontSize }}
														className={isForPrint ? 'text-dark' : ''}
													>
														<Stack>
															<span
																style={{
																	color: '#00a152'
																}}
															>{`Pagadas: ${
																col.getValue(r).cuotasPagadas
															}`}</span>
															<span
																style={{
																	color: '#f50057'
																}}
															>{`Pendientes: ${
																col.getValue(r).cuotasPendiente
															}`}</span>
														</Stack>
													</TableCell>
												);
											} else {
												return (
													<TableCell
														key={col.key}
														align={col.align}
														style={{ fontSize: dataFontSize }}
														className={isForPrint ? 'text-dark' : ''}
													>
														{col.getValue(r)}
													</TableCell>
												);
											}
										})}
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
				<hr />
				<Stack direction="row" flex="row" justifyContent="space-around">
					<Stack textAlign="center">
						<Typography
							style={{
								fontWeight: 'bold',
								color: '#03a9f4',
								fontSize: 11
							}}
						>
							Total Créditos
						</Typography>
						<Typography style={{ fontSize: 11 }}>{dataGestor.length}</Typography>
					</Stack>

					<Stack textAlign="center">
						<Typography
							style={{
								fontWeight: 'bold',
								color: '#03a9f4',
								fontSize: 11
							}}
						>
							Total Capital
						</Typography>
						<Typography style={{ fontSize: 11 }}>
							{new Intl.NumberFormat('es-NI', {
								style: 'currency',
								currency: 'NIO'
							}).format(totalCapital)}
						</Typography>
					</Stack>

					<Stack textAlign="center">
						<Typography
							style={{
								fontWeight: 'bold',
								color: '#03a9f4',
								fontSize: 11
							}}
						>
							Saldo Capital Total
						</Typography>
						<Typography style={{ fontSize: 11 }}>
							{new Intl.NumberFormat('es-NI', {
								style: 'currency',
								currency: 'NIO'
							}).format(totalSaldoCapital)}
						</Typography>
					</Stack>

					<Stack textAlign="center">
						<Typography
							style={{
								fontWeight: 'bold',
								color: '#03a9f4',
								fontSize: 11
							}}
						>
							Saldo I.C. Total
						</Typography>
						<Typography style={{ fontSize: 11 }}>
							{new Intl.NumberFormat('es-NI', {
								style: 'currency',
								currency: 'NIO'
							}).format(totalSaldoIC)}
						</Typography>
					</Stack>

					<Stack textAlign="center">
						<Typography
							style={{
								fontWeight: 'bold',
								color: '#03a9f4',
								fontSize: 11
							}}
						>
							Saldo Total
						</Typography>
						<Typography style={{ fontSize: 11 }}>
							{new Intl.NumberFormat('es-NI', {
								style: 'currency',
								currency: 'NIO'
							}).format(totalSaldo)}
						</Typography>
					</Stack>
				</Stack>
				<hr />
			</>
		);
	};

	const consolidatedTotals = useMemo(() => {
		if (!data.length) {
			return {
				totalCapital: 0,
				totalSaldoCapital: 0,
				totalSaldoIC: 0,
				totalSaldo: 0
			};
		}
		return data.reduce(
			(acc, c) => {
				acc.totalCapital += c.montoEntregado;
				acc.totalSaldoCapital += c.saldoCapital;
				acc.totalSaldoIC += c.saldoIC;
				acc.totalSaldo += c.saldoTotal;

				return acc;
			},
			{
				totalCapital: 0,
				totalSaldoCapital: 0,
				totalSaldoIC: 0,
				totalSaldo: 0
			}
		);
	}, [data]);

	const { totalCapital, totalSaldoCapital, totalSaldoIC, totalSaldo } = consolidatedTotals;

	const footer = (
		<Stack>
			<Typography
				sx={{
					color: '#2196f3',
					textAlign: 'center'
				}}
			>
				Consolidado
			</Typography>

			<hr />

			<Stack direction="row" flex="row" justifyContent="space-around">
				<Stack textAlign="center">
					<Typography
						style={{
							fontWeight: 'bold',
							color: '#03a9f4',
							fontSize: 11
						}}
					>
						Total Créditos
					</Typography>
					<Typography style={{ fontSize: 11 }}>{data.length}</Typography>
				</Stack>

				<Stack textAlign="center">
					<Typography
						style={{
							fontWeight: 'bold',
							color: '#03a9f4',
							fontSize: 11
						}}
					>
						Total Capital
					</Typography>
					<Typography style={{ fontSize: 11 }}>
						{new Intl.NumberFormat('es-NI', {
							style: 'currency',
							currency: 'NIO'
						}).format(totalCapital)}
					</Typography>
				</Stack>

				<Stack textAlign="center">
					<Typography
						style={{
							fontWeight: 'bold',
							color: '#03a9f4',
							fontSize: 11
						}}
					>
						Saldo Capital Total
					</Typography>
					<Typography style={{ fontSize: 11 }}>
						{new Intl.NumberFormat('es-NI', {
							style: 'currency',
							currency: 'NIO'
						}).format(totalSaldoCapital)}
					</Typography>
				</Stack>

				<Stack textAlign="center">
					<Typography
						style={{
							fontWeight: 'bold',
							color: '#03a9f4',
							fontSize: 11
						}}
					>
						Saldo I.C. Total
					</Typography>
					<Typography style={{ fontSize: 11 }}>
						{new Intl.NumberFormat('es-NI', {
							style: 'currency',
							currency: 'NIO'
						}).format(totalSaldoIC)}
					</Typography>
				</Stack>

				<Stack textAlign="center">
					<Typography
						style={{
							fontWeight: 'bold',
							color: '#03a9f4',
							fontSize: 11
						}}
					>
						Total Saldo
					</Typography>
					<Typography style={{ fontSize: 11 }}>
						{new Intl.NumberFormat('es-NI', {
							style: 'currency',
							currency: 'NIO'
						}).format(totalSaldo)}
					</Typography>
				</Stack>
			</Stack>
			<hr />
		</Stack>
	);

	return (
		<div>
			<Dialog fullScreen open={true}>
				<AppBar sx={{ position: 'relative' }}>
					<Toolbar>
						<img loading="eager" src={icon} alt="logo" style={{ height: 40 }} />
						<Typography
							sx={{ ml: 2, flex: 1, textAlign: 'center' }}
							variant="h5"
							component="div"
						>
							{`${title} `}
						</Typography>
						<img loading="eager" src={icon} alt="logo" style={{ height: 40 }} />
					</Toolbar>
				</AppBar>

				<Stack display="flex" justifyContent="center">
					<Stack
						direction={'row'}
						justifyContent="space-between"
						paddingLeft={5}
						paddingRight={5}
						marginTop={1}
						marginBottom={1}
					>
						<IconButton variant="outlined" onClick={() => exportExcel()}>
							<FontAwesomeIcon
								icon={faCloudArrowDown}
								style={{ fontSize: 40, color: '#00a152', width: 40 }}
							/>
						</IconButton>

						<Stack>
							<Typography
								sx={{
									color: '#2196f3',
									textAlign: 'center',
									fontWeight: 'bold'
								}}
							>
								{slogan}
							</Typography>
							<Typography
								sx={{
									color: '#2196f3',
									textAlign: 'center',
									fontWeight: 'bold'
								}}
								variant="h6"
								component="div"
							>
								Reporte de Créditos Saneados
							</Typography>
						</Stack>

						<ReactToPrint
							trigger={() => {
								return (
									<IconButton
										variant="outlined"
										// style={{ position: "fixed", right: 20, top: 75 }}
									>
										<PrintRoundedIcon
											style={{ fontSize: 40, color: '#2979ff', width: 40 }}
										/>
									</IconButton>
								);
							}}
							content={() => compRef.current}
							pageStyle={pageStyle}
						/>
					</Stack>

					{paramText}
				</Stack>

				<Container maxWidth={false} sx={{ textAlign: 'center' }}>
					<hr />
					{isEmpty(data) ? (
						<NoData />
					) : (
						<>
							{gestorList.map((gestor) => {
								const cGestor = data.filter((c) => c.gestor === gestor.id);

								return (
									<Stack key={gestor.id}>
										<Stack direction="row" spacing={1}>
											<Typography
												sx={{
													color: '#2196f3',
													textAlign: 'left'
												}}
												fontSize={11}
											>
												Gestor:
											</Typography>
											<Typography
												sx={{
													textAlign: 'left'
												}}
												fontSize={11}
											>
												{gestor.fullName}
											</Typography>
										</Stack>

										<Divider />

										<DataTable
											dataGestor={cGestor}
											headerFontSize={11}
											dataFontSize={10}
										/>
									</Stack>
								);
							})}
						</>
					)}

					{gestorList.length === 1 ? null : footer}
				</Container>
			</Dialog>

			<div
				style={{
					display: 'none'
				}}
			>
				<PrintReport
					ref={compRef}
					titulo={'Reporte de Créditos Saneados'}
					fecha={`Fecha: ${moment().format('L')}`}
				>
					<Container maxWidth={false} sx={{ textAlign: 'center' }}>
						{paramText}

						<Container maxWidth={false} sx={{ textAlign: 'center' }}>
							<hr />
							{isEmpty(data) ? (
								<NoData />
							) : (
								<>
									{gestorList.map((gestor) => {
										const cGestor = data.filter((c) => c.gestor === gestor.id);

										return (
											<Stack key={gestor.id}>
												<Stack direction="row" spacing={1}>
													<Typography
														sx={{
															color: '#2196f3',
															textAlign: 'left'
														}}
														fontSize={11}
													>
														Gestor:
													</Typography>
													<Typography
														sx={{
															textAlign: 'left'
														}}
														fontSize={11}
													>
														{gestor.fullName}
													</Typography>
												</Stack>

												<Divider />

												<DataTable
													dataGestor={cGestor}
													headerFontSize={10}
													dataFontSize={9}
													isForPrint={true}
												/>
											</Stack>
										);
									})}
								</>
							)}

							{gestorList.length === 1 ? null : footer}
						</Container>
					</Container>
				</PrintReport>
			</div>
		</div>
	);
};

export default CreditosSaneados;
